/**
 * InkGenerationOverlay — the AI "writing" moment with a real ink-smoke particle
 * field (game-style). Two phases:
 *   1. BLOOM  (text === null): ink-smoke rises while the model generates.
 *   2. REVEAL (text set): the prose writes itself word-by-word, and EACH word
 *      puffs a wisp of ink-smoke that rises and dissipates.
 *
 * The smoke is a canvas particle system fed imperatively from the reveal, so it
 * stays smooth (one rAF loop, capped particle count). Tap to skip.
 */
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';

interface InkGenerationOverlayProps {
  open: boolean;
  /** null = still generating (bloom). A string = reveal that prose. */
  text: string | null;
  onRevealDone: () => void;
  onSkip: () => void;
}

// ── Ink-smoke particle field ────────────────────────────────────────────────

interface SmokeHandle {
  /** Emit a wisp of smoke at viewport coords (x, y). */
  emit: (x: number, y: number, count?: number) => void;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  grow: number;
  age: number;
  life: number;
  amber: boolean;
  seed: number;
}

const SmokeField = forwardRef<SmokeHandle, { ambient?: boolean }>(function SmokeField(
  { ambient = false },
  ref,
) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const particles = useRef<Particle[]>([]);
  const dprRef = useRef(1);

  useImperativeHandle(ref, () => ({
    emit(x: number, y: number, count = 5) {
      const dpr = dprRef.current;
      for (let i = 0; i < count; i++) {
        const t = particles.current.length + i;
        particles.current.push({
          x: (x + (((t * 53) % 14) - 7)) * dpr,
          y: (y + (((t * 31) % 8) - 4)) * dpr,
          vx: (((t * 17) % 100) / 100 - 0.5) * 0.5 * dpr,
          vy: (-0.45 - ((t * 13) % 40) / 100) * dpr,
          r: (5 + ((t * 7) % 5)) * dpr,
          grow: (0.18 + ((t * 11) % 20) / 100) * dpr,
          age: 0,
          life: 1300 + ((t * 29) % 1100),
          amber: t % 4 === 0,
          seed: t,
        });
      }
      if (particles.current.length > 360) {
        particles.current.splice(0, particles.current.length - 360);
      }
    },
  }));

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    dprRef.current = dpr;

    function resize() {
      if (!canvas) return;
      canvas.width = canvas.clientWidth * dpr;
      canvas.height = canvas.clientHeight * dpr;
    }
    resize();
    window.addEventListener('resize', resize);

    let raf = 0;
    let last = 0;
    let ambientAcc = 0;
    let running = true;

    function frame(now: number) {
      if (!running || !canvas || !ctx) return;
      if (!last) last = now;
      const dt = Math.min(48, now - last);
      last = now;

      // soft trail clear
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // ambient emission (bloom phase) — gentle rising column from centre
      if (ambient) {
        ambientAcc += dt;
        while (ambientAcc > 60) {
          ambientAcc -= 60;
          const cx = canvas.width * (0.5 + Math.sin(now / 1800) * 0.06) / dpr;
          const cy = canvas.height * 0.6 / dpr;
          particles.current.push({
            x: cx * dpr,
            y: cy * dpr,
            vx: ((now / 91) % 1 - 0.5) * 0.5 * dpr,
            vy: (-0.5 - ((now / 53) % 0.4)) * dpr,
            r: 7 * dpr,
            grow: 0.22 * dpr,
            age: 0,
            life: 1700,
            amber: (Math.floor(now / 60) % 4) === 0,
            seed: now,
          });
        }
      }

      ctx.globalCompositeOperation = 'lighter';
      const arr = particles.current;
      for (let i = arr.length - 1; i >= 0; i--) {
        const p = arr[i];
        p.age += dt;
        if (p.age >= p.life) {
          arr.splice(i, 1);
          continue;
        }
        const k = p.age / p.life; // 0→1
        // rising + buoyancy + turbulence
        p.vy -= 0.0006 * dt * dpr;
        p.x += p.vx * dt * 0.06 + Math.sin((p.age + p.seed) / 240) * 0.25 * dpr;
        p.y += p.vy * dt * 0.06;
        p.r += p.grow * dt * 0.06;
        const alpha = Math.sin(Math.min(1, k) * Math.PI) * 0.16; // 0→peak→0
        const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, Math.max(1, p.r));
        if (p.amber) {
          g.addColorStop(0, `rgba(223,201,147,${alpha})`);
          g.addColorStop(0.5, `rgba(176,128,52,${alpha * 0.4})`);
        } else {
          g.addColorStop(0, `rgba(150,166,214,${alpha})`);
          g.addColorStop(0.5, `rgba(90,104,150,${alpha * 0.4})`);
        }
        g.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(p.x, p.y, Math.max(1, p.r), 0, Math.PI * 2);
        ctx.fill();
      }

      raf = requestAnimationFrame(frame);
    }
    raf = requestAnimationFrame(frame);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, [ambient]);

  return <canvas ref={canvasRef} className="pointer-events-none absolute inset-0 h-full w-full" />;
});

// ── Word-by-word reveal that feeds the smoke ────────────────────────────────

function InkReveal({ text, onWord, onDone }: { text: string; onWord: (x: number, y: number) => void; onDone: () => void }) {
  const paragraphs = useMemo(
    () =>
      text
        .split(/\n{2,}/)
        .map((p) => p.trim())
        .filter(Boolean)
        .map((p) => p.split(/\s+/)),
    [text],
  );
  const total = useMemo(() => paragraphs.reduce((n, p) => n + p.length, 0), [paragraphs]);
  const [shown, setShown] = useState(0);
  const wordRef = useRef<HTMLSpanElement | null>(null);

  // Emit smoke from the just-revealed word.
  useEffect(() => {
    if (shown === 0) return;
    const el = wordRef.current;
    if (el) {
      const r = el.getBoundingClientRect();
      onWord(r.left + r.width / 2, r.top + r.height * 0.5);
    }
  }, [shown, onWord]);

  useEffect(() => {
    if (shown >= total) {
      const t = window.setTimeout(onDone, 700);
      return () => window.clearTimeout(t);
    }
    // find current word to time the pause
    let word = '';
    let i = 0;
    outer: for (const p of paragraphs) {
      for (const w of p) {
        if (i === shown) { word = w; break outer; }
        i++;
      }
    }
    const pause = /[.!?…]["')]?$/.test(word) ? 230 : /[,;:]$/.test(word) ? 130 : 34;
    const t = window.setTimeout(() => setShown((s) => s + 1), pause);
    return () => window.clearTimeout(t);
  }, [shown, total, paragraphs, onDone]);

  let idx = 0;
  return (
    <div className="relative mx-auto max-w-[640px] px-6 font-serif leading-relaxed" style={{ fontSize: 19, color: '#E7ECF7' }}>
      {paragraphs.map((words, pi) => (
        <p key={pi} className="mb-4">
          {words.map((w) => {
            const myIdx = idx++;
            const visible = myIdx < shown;
            const isCurrent = myIdx === shown - 1;
            return (
              <span key={myIdx}>
                <motion.span
                  ref={isCurrent ? wordRef : undefined}
                  initial={{ opacity: 0, filter: 'blur(6px)' }}
                  animate={visible ? { opacity: 1, filter: 'blur(0px)' } : { opacity: 0 }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  style={{ display: 'inline-block', willChange: 'filter, opacity' }}
                >
                  {w}
                </motion.span>{' '}
              </span>
            );
          })}
        </p>
      ))}
    </div>
  );
}

// ── Overlay ─────────────────────────────────────────────────────────────────

export function InkGenerationOverlay({ open, text, onRevealDone, onSkip }: InkGenerationOverlayProps) {
  const phase: 'bloom' | 'reveal' = text == null ? 'bloom' : 'reveal';
  const smokeRef = useRef<SmokeHandle>(null);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[200] flex flex-col items-center justify-center overflow-hidden"
          style={{
            background: 'radial-gradient(circle at 50% 42%, #0f1426 0%, #0a0e1c 70%)',
            paddingTop: 'env(safe-area-inset-top)',
            paddingBottom: 'env(safe-area-inset-bottom)',
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.5 } }}
          onClick={() => { if (phase === 'reveal') onSkip(); }}
        >
          <SmokeField ref={smokeRef} ambient={phase === 'bloom'} />

          <div className="relative z-10 w-full">
            {phase === 'bloom' ? (
              <div className="flex flex-col items-center gap-4">
                <motion.div
                  className="h-2 w-2 rounded-full"
                  style={{ background: '#DFC993', boxShadow: '0 0 24px rgba(223,201,147,0.9)' }}
                  animate={{ scale: [1, 1.8, 1], opacity: [0.6, 1, 0.6] }}
                  transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
                />
                <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-[#9DB0E6]">
                  A IA está escrevendo
                </p>
              </div>
            ) : (
              <InkReveal
                text={text as string}
                onWord={(x, y) => smokeRef.current?.emit(x, y, 5)}
                onDone={onRevealDone}
              />
            )}
          </div>

          {phase === 'reveal' && (
            <p
              className="absolute font-mono text-[10px] uppercase tracking-widest text-white/25"
              style={{ bottom: 'calc(1.5rem + env(safe-area-inset-bottom))' }}
            >
              Toque para pular
            </p>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
